import { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import ChatComposer from "./ChatComposer";
import ChatMessageList from "./ChatMessageList";
import NeoAvatar from "./NeoAvatar";
import { chatbotPalette } from "./chatbotTheme";

const pageLabels = { oee_production: "การผลิต", oee_quality: "คุณภาพ", oee_maintenance: "การบำรุงรักษา", oee_energy: "พลังงาน" };

export default function ChatbotPanel({ open, onClose, onClear, onSubmit, title = "Neo", ...chat }) {
  const [expanded, setExpanded] = useState(false);
  const closeRef = useRef(null);
  const { pageContext, pending, liveAssistantMessage, messages = [], error } = chat;
  const state = pending ? (liveAssistantMessage?.mode === "final" ? "answer" : "think") : "idle";
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    closeRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, [open]);

  return (
    <Box id="neo-chat-panel" role="dialog" aria-label={title} hidden={!open}
      onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); onClose(); } }}
      sx={{ position: "fixed", top: { xs: 56, md: 76 }, right: { xs: 6, md: 20 }, bottom: { xs: 6, md: 20 },
        width: { xs: "calc(100vw - 12px)", sm: expanded ? "min(900px, calc(100vw - 40px))" : 480 },
        maxWidth: "calc(100vw - 12px)", display: open ? "flex" : "none", flexDirection: "column",
        borderRadius: 4, overflow: "hidden", background: "#253C50", color: chatbotPalette.textMain,
        border: "1px solid #354c61", boxShadow: "0 18px 64px #0007", zIndex: 5,
        "@media (prefers-reduced-motion: reduce)": { "& *": { animation: "none !important", scrollBehavior: "auto !important" } } }}>
      <Box sx={{ px: 2, py: 1.3, display: "flex", alignItems: "center", gap: 1.2, background: "#35566C", flexShrink: 0 }}>
        <NeoAvatar size={46} state={state} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{title}</Typography>
          <Typography role="status" sx={{ color: chatbotPalette.teal, fontSize: 12 }}>
            {pending ? (state === "answer" ? "กำลังตอบคำถาม" : "กำลังตรวจสอบข้อมูล…") : error ? "ลองส่งคำถามอีกครั้งได้ครับ" : "พร้อมช่วยดูข้อมูลครับ"}
          </Typography>
        </Box>
        <IconButton ref={closeRef} onClick={onClose} aria-label="ปิดแชต" sx={{ color: "#d9e8f3" }}><CloseRoundedIcon /></IconButton>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 2, py: 1.2, borderBottom: "1px solid #476277", flexShrink: 0 }}>
        {[pageContext?.line ? String(pageContext.line).replace(/^line\s*/i, "ไลน์ ") : null, pageLabels[pageContext?.page]].filter(Boolean).map(label => (
          <Chip key={label} label={label} size="small" sx={{ color: "#c7e9e1", background: "#3C626D" }} />
        ))}
        <Typography sx={{ fontSize: 11, color: chatbotPalette.textMuted, alignSelf: "center" }}>บริบทจากหน้าที่เปิด</Typography>
      </Box>
      <Box sx={{ px: 2, py: 0.5, display: "flex", justifyContent: "space-between", gap: 1, flexShrink: 0 }}>
        <Button onClick={() => setExpanded(v => !v)} aria-pressed={expanded}
          startIcon={expanded ? <CloseFullscreenRoundedIcon /> : <OpenInFullRoundedIcon />}
          sx={{ fontSize: 12, color: chatbotPalette.teal }}>
          {expanded ? "ย่อหน้าต่าง" : "ขยายการวิเคราะห์"}
        </Button>
        <Button onClick={onClear} disabled={!messages.length || pending} sx={{ fontSize: 12, color: chatbotPalette.textMuted, "&.Mui-disabled": { color: "#9CAEBB" } }}>ล้างแชต</Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, px: { xs: 1.5, sm: 2.5 }, py: 1.5, display: "flex", overflow: "hidden" }}>
        <ChatMessageList {...chat} />
      </Box>
      <Box sx={{ px: 2, py: 1.5, background: "#2D495F", borderTop: "1px solid #476277", flexShrink: 0 }}>
        <ChatComposer disabled={pending} onSubmit={onSubmit} />
      </Box>
    </Box>
  );
}
